import {useMaterialsSnapshot} from '@domain/materials'
import {SummaryPageData, useRequiredConfig} from '@domain/shifthandover'
import {DateRange} from '@hconnect/common'
import {useUpmPlant, useUpmPlantTimezone} from '@hconnect/upm'
import {Grid, Theme, useMediaQuery} from '@mui/material'
import {ExpertSystemWrapper, useExpertSystemEquipmentsWithNames} from '@widgets/expert-system'
import {MaterialQualityParametersContainer} from '@widgets/kpis'
import {FilterOptions} from '@widgets/shift-event-filters'
import {FC} from 'react'

import {ProductionContainer} from '../components'
import {useFilterDatePicker} from '../hooks'

import {RunningTimesSummaryContainer} from './RunningTimesSummaryContainer'
import {ShiftComments} from './ShiftComments'
import {ShiftSummaryEvents} from './ShiftSummaryEvents'
import {SiloFillsContainer} from './SiloFillsContainer'

type ShiftSummaryViewProps = {
  plantId: string
  isMobileFilterOpen: boolean
  isLoading: boolean
  isFetching: boolean
  summaryPageData?: SummaryPageData
  isProductionVolumesAllowed: boolean
  filterOptions: FilterOptions
  isExpertSystemAllowed: boolean
  isSiloFillAllowed: boolean
  dashboardUrl: string
  onEquipmentClick: (equipmentId: string, dateRange: DateRange) => void
}

export const ShiftSummaryView: FC<ShiftSummaryViewProps> = ({
  plantId,
  isMobileFilterOpen,
  isLoading,
  isFetching,
  summaryPageData,
  isProductionVolumesAllowed,
  isExpertSystemAllowed,
  isSiloFillAllowed,
  filterOptions: filterOptionsProps,
  dashboardUrl,
  onEquipmentClick
}) => {
  const {shifts, pxTrendAvailable} = useRequiredConfig(plantId)
  const timezone = useUpmPlantTimezone(plantId)
  const {upmId: upmPlantId} = useUpmPlant({plantId})
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'))
  const {isSingleShift, quickSelectSlot, timeRange, dateRange} = useFilterDatePicker(
    plantId,
    shifts
  )
  const {data: materialsData, isLoading: isMaterialsDataLoading} = useMaterialsSnapshot({
    plantCode: plantId,
    snapshotDateTime: timeRange.endDate,
    options: {
      enabled: isSingleShift
    }
  })

  const contentMode = 'contentDependentHeight'
  const isExpertSystemEnabled = [
    'currentShift',
    'pastShift',
    'today',
    'last24hours',
    'yesterday',
    'lastWeekend',
    'last3days',
    'last7days',
    'last30days'
  ].includes(quickSelectSlot)

  const {
    data: expertSystemEquipments,
    error,
    isLoading: expertSystemEquipmentsLoading,
    isFetching: expertSystemEquipmentsFetching
  } = useExpertSystemEquipmentsWithNames(
    {
      hierarchyId: upmPlantId,
      from: timeRange.startDate,
      to: timeRange.endDate
    },
    {
      enabled: isExpertSystemEnabled
    }
  )

  const expertSystemEquipmentsFetchingOrLoading =
    expertSystemEquipmentsFetching || expertSystemEquipmentsLoading

  const isLoadingOrFetching = isLoading || isFetching

  return !isMobileFilterOpen ? (
    <Grid container spacing={2} width="100%" wrap={isMobile ? undefined : 'nowrap'}>
      <Grid item container spacing={2} direction="column" sm={12} md={4}>
        <Grid item>
          <ShiftComments
            plantId={plantId}
            timeRange={timeRange}
            isSingleShift={isSingleShift}
            contentMode={contentMode}
            timezone={timezone}
          />
        </Grid>

        {isExpertSystemAllowed && (
          <Grid item>
            <ExpertSystemWrapper
              canDisplayMultiMonthData={isExpertSystemEnabled}
              expertSystemEquipments={expertSystemEquipments}
              isLoading={isLoadingOrFetching || expertSystemEquipmentsFetchingOrLoading}
              error={error}
            />
          </Grid>
        )}

        <Grid item width="100%">
          <MaterialQualityParametersContainer
            plantId={plantId}
            cardBoxSx={{flex: 1, maxHeight: 450}}
            from={timeRange.startDate}
            to={timeRange.endDate}
            placeholderWrapperSx={{height: '100%'}}
            timezone={timezone}
            isVerticalLayout={true}
          />
        </Grid>
        {isProductionVolumesAllowed && (
          <Grid item>
            <ProductionContainer
              isLoading={isLoadingOrFetching || isMaterialsDataLoading}
              materials={materialsData}
              productionVolumes={summaryPageData?.productionVolumesResult?.productionVolumes}
              contentMode={contentMode}
              timeRange={timeRange}
              timezone={timezone}
            />
          </Grid>
        )}
      </Grid>

      {(pxTrendAvailable || isSiloFillAllowed) && (
        <Grid item container spacing={2} direction="column" sm={12} md={4}>
          {pxTrendAvailable && (
            <Grid item>
              <RunningTimesSummaryContainer
                plantId={plantId}
                contentMode={contentMode}
                isLoading={isLoading}
                dateRange={dateRange}
                dashboardUrl={dashboardUrl}
                onEquipmentClick={(equipmentId: string) => onEquipmentClick(equipmentId, dateRange)}
              />
            </Grid>
          )}

          {isSiloFillAllowed && (
            <Grid item width="100%">
              <SiloFillsContainer
                plantId={plantId}
                contentMode={contentMode}
                isSingleShift={isSingleShift}
                timeRange={timeRange}
              />
            </Grid>
          )}
        </Grid>
      )}

      <Grid item container spacing={2} direction="column" sm={12} md={4}>
        <Grid item>
          <ShiftSummaryEvents plantId={plantId} filterOptions={filterOptionsProps} />
        </Grid>
      </Grid>
    </Grid>
  ) : (
    <></>
  )
}
